/**
 * SegmentCard — gallery tile for a single market segment.
 *
 * Single-column layout, full card width used for each content row:
 *   1. Meta row: sector tag (left) + status / beachhead pills (right)
 *   2. Title block: name (hero) + optional vertical subtitle; weighted score
 *      rendered as a large inline number aligned right.
 *   3. Description (2-line clamp, warm secondary tone)
 *   4. Sizing strip — inline TAM · SAM · SOM · CAGR, no boxed card-in-card.
 *   5. Footer — persona avatars + updated + open hint.
 */

import type { KeyboardEvent } from 'react'
import type { MarketPersona, MarketSegmentWithScore } from '@ispot/shared'
import { formatMoney, formatRelative, tierFor } from './segmentCardUtils'
import {
  AvatarStack,
  BeachheadPill,
  CagrChip,
  InlineMetric,
  ScoreChip,
  StatusPill,
} from './SegmentCardParts'

interface Props {
  segment: MarketSegmentWithScore
  isBeachhead: boolean
  selected?: boolean
  personas: MarketPersona[]
  personaCount: number
  onOpen: () => void
}

export function SegmentCard({ segment, isBeachhead, selected = false, personas, personaCount, onOpen }: Props) {
  const weighted = segment.weightedScore
  const hasScore = weighted > 0
  const scoreTier = tierFor(weighted)

  const isDraft = segment.status === 'draft'
  const isArchived = segment.status === 'archived'

  const cagr = segment.sizing.cagr?.value ?? null
  const cagrTrend: 'pos' | 'neg' | 'flat' =
    cagr == null ? 'flat' : cagr > 0 ? 'pos' : cagr < 0 ? 'neg' : 'flat'

  const sectorLabel = segment.sector === 'cross' ? 'Cross' : segment.sector === 'public' ? 'Public' : 'Private'
  const metaText = segment.vertical ? `${sectorLabel} · ${segment.vertical}` : sectorLabel

  const handleKeyDown = (e: KeyboardEvent<HTMLElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      onOpen()
    }
  }

  const displayPersonas = personas.slice(0, 4)
  const overflow = Math.max(0, personaCount - displayPersonas.length)

  return (
    <article
      role="button"
      tabIndex={0}
      aria-label={`Open ${segment.name}`}
      onClick={onOpen}
      onKeyDown={handleKeyDown}
      data-testid={`segment-card-${segment.id}`}
      data-beachhead={isBeachhead || undefined}
      data-status={segment.status}
      data-selected={selected || undefined}
      className={[
        'group relative flex cursor-pointer flex-col overflow-hidden rounded-[18px] bg-white transition-all duration-200',
        'ring-1 ring-[rgba(29,26,21,0.07)] shadow-[0_1px_2px_rgba(29,26,21,0.03),0_8px_24px_rgba(29,26,21,0.05)]',
        'hover:-translate-y-0.5 hover:ring-[rgba(29,26,21,0.1)] hover:shadow-[0_10px_28px_rgba(29,26,21,0.08)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-accent,#e84300)] focus-visible:ring-offset-2',
        selected ? 'ring-[var(--color-business-300,#6fafe8)] shadow-[0_12px_32px_rgba(49,150,226,0.14)]' : '',
        isArchived ? 'opacity-80' : '',
      ].join(' ')}
    >
      <div className="flex flex-col gap-4 px-5 pt-5 pb-4">
        {/* META ROW — sector left, pills right */}
        <div className="flex items-start justify-between gap-3">
          <span
            className="inline-flex items-center rounded-full px-2 py-[3px] text-[10px] font-semibold uppercase tracking-[0.1em]"
            style={{
              color: isDraft || isArchived ? 'var(--color-text-muted)' : 'var(--color-market-700, #15747a)',
              backgroundColor: isDraft || isArchived ? 'var(--color-warm-200)' : 'var(--color-market-100, #c9f0f2)',
            }}
          >
            {metaText}
          </span>
          <div className="flex shrink-0 items-center gap-1.5">
            {isBeachhead ? <BeachheadPill /> : null}
            <StatusPill status={segment.status} />
          </div>
        </div>

        {/* TITLE BLOCK — name hero + inline score */}
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0 flex-1">
            <h3 className="font-display text-[22px] font-bold leading-[1.15] tracking-[-0.015em]">
              {segment.name}
            </h3>
            {segment.vertical ? (
              <p className="mt-1 truncate text-[12px]" style={{ color: 'var(--color-text-muted)' }}>
                {segment.vertical}
              </p>
            ) : null}
          </div>
          <ScoreChip score={weighted} hasScore={hasScore} tier={scoreTier} />
        </div>

        {/* DESCRIPTION — optional, 2-line clamp */}
        <p
          className="line-clamp-2 text-[13px] leading-[1.55]"
          style={{ color: segment.description ? 'var(--color-text-secondary)' : 'var(--color-text-muted)' }}
        >
          {segment.description && segment.description.trim().length > 0 ? (
            segment.description
          ) : (
            <span className="italic">
              No description yet — open to let Skerpa draft one from your sources.
            </span>
          )}
        </p>

        {/* SIZING STRIP — inline, no nested card */}
        <div className="flex flex-wrap items-baseline gap-x-5 gap-y-2">
          <InlineMetric label="TAM" value={formatMoney(segment.sizing.tam?.value)} />
          <InlineMetric
            label="SAM"
            value={formatMoney(segment.sizing.samTopDown?.value ?? segment.sizing.samBottomUp?.value)}
          />
          <InlineMetric label="SOM" value={formatMoney(segment.sizing.som?.value)} />
          <CagrChip value={cagr} trend={cagrTrend} />
        </div>

        {/* FOOTER */}
        <div
          className="mt-1 flex items-center justify-between gap-3 border-t pt-3 text-[11px]"
          style={{
            borderColor: 'var(--color-border-subtle)',
            color: 'var(--color-text-muted)',
          }}
        >
          <div className="inline-flex min-w-0 items-center gap-3">
            <span className="shrink-0">
              {personaCount} {personaCount === 1 ? 'persona' : 'personas'}
            </span>
            {displayPersonas.length > 0 ? <AvatarStack personas={displayPersonas} overflow={overflow} /> : null}
            <span className="h-[3px] w-[3px] shrink-0 rounded-full" style={{ backgroundColor: 'var(--color-warm-300)' }} />
            <span className="truncate">Updated {formatRelative(segment.updatedAt)}</span>
          </div>
          <span
            className="inline-flex shrink-0 items-center gap-1 text-[11px] font-medium opacity-0 transition-opacity duration-150 group-hover:opacity-100 group-focus-visible:opacity-100"
            style={{ color: 'var(--color-text)' }}
          >
            Open
            <svg className="h-3 w-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <line x1="5" y1="12" x2="19" y2="12" />
              <polyline points="12 5 19 12 12 19" />
            </svg>
          </span>
        </div>
      </div>
    </article>
  )
}
